import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { connect } from 'react-redux';

import {
  useErrorHandling,
  useFileValidation,
  usePresignedUrl,
  useRequestUpload,
  captureException,
  validateVideoQuality,
  axiosThirdParty,
} from '@playbookux/playbook-web-core';

import {
  AcceptedFilesText,
  Container,
  ContainerBox,
  Dialog,
  ErrorMessage,
  InputFile,
  Link,
  ProgressContainer,
  Text,
  Title,
  UploadBox,
} from './styles';
import UploadFileProgress from '../upload-file-progress';

import { CREATE_ATTACHMENT, CREATE_PROJECT_UPLOAD_RESEARCH_RECORD, GET_COMPANY_AVAILABLE_STORAGE } from './graphql';
import { GET_PROJECT_ATTACHMENTS } from '../attachments/graphql';

import client from '../../../core/services/client';

const uploadPolicyUrl = 'https://playbookux.com/acceptable-use-policy/';

/**
 * This file is only used for Attachments and Upload project type
 */
const Uploader = (props) => {
  const {
    title,
    additionalText,
    onClose,
    show,
    onUploadCompleted,
    projectId,
    companyId,
    uploadAttachment,
  } = props;

  useEffect(() => {
    axiosThirdParty.setDismounted(false);
    return () => {
      axiosThirdParty.setDismounted(true);
      axiosThirdParty.abortWhenDismounted();
    };
  }, []);

  const [error, setError] = useState('');
  const [files, setFiles] = useState([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const fileRef = useRef(null);

  const [uploadProgress, setUploadProgress] = useState([]);
  const [createProjectUploadRecords] = useMutation(CREATE_PROJECT_UPLOAD_RESEARCH_RECORD, {
    onCompleted: () => {
      if (onUploadCompleted) {
        onUploadCompleted();
      }
    },
  });

  const {
    data: { company: { uploadResearchAvailableStorage = '' } = {} } = {},
  } = useQuery(GET_COMPANY_AVAILABLE_STORAGE, {
    variables: { id: companyId },
    skip: !companyId,
    fetchPolicy: 'no-cache',
  });

  const createRecords = async (object) => {
    try {
      await createProjectUploadRecords({
        variables: {
          projectId,
          fileAttributes: {
            name: object.filename,
            size: object.filesize,
          },
        },
      });
    } catch (e) {
      captureException('HANDLE_CREATE_RECORDS_FAILED', e);
    }
  };

  const [createAttachments] = useMutation(CREATE_ATTACHMENT, {
    refetchQueries: [
      {
        query: GET_PROJECT_ATTACHMENTS,
        variables: {
          projectId,
        },
      },
    ],
  });

  const createAttachment = async (object) => {
    try {
      await createAttachments({
        variables: {
          projectId,
          filename: object.filename,
        },
      });
    } catch (e) {
      captureException('HANDLE_CREATE_ATTACHMENT_FAILED', e);
    }
  };

  const createUploadMutation = uploadAttachment ? createAttachment : createRecords;
  const endpointUrl = uploadAttachment ? 'presigned_url_attachment' : 'presigned_url_upload_project';

  const { errorUploadedFiles, setErrorUploadedFilesIndex } = useErrorHandling();
  const { getPresignedUrlCallback } = usePresignedUrl(
    {
      project_id: projectId,
    },
    setErrorUploadedFilesIndex,
    endpointUrl,
    client,
  );

  const normalizedGetPresignedUrlCallback = useCallback(
    async (filename, filesize, index, contentType) => {
      const normalizedFilename = filename.replace(/\s+/g, '_');
      return getPresignedUrlCallback(normalizedFilename, filesize, index, contentType);
    },
    [getPresignedUrlCallback],
  );

  const { requestUpload } = useRequestUpload(
    projectId,
    createUploadMutation,
    setErrorUploadedFilesIndex,
    setUploadProgress,
    'Each file should be 1.5 GB or smaller',
  );

  const { validateAndSetFiles } = useFileValidation(
    files,
    setFiles,
    // others
    uploadAttachment, // this is attachment
    uploadResearchAvailableStorage,
    // state
    setErrorUploadedFilesIndex,
    setError,
    // hooks
    normalizedGetPresignedUrlCallback,
    requestUpload,
  );

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOver(false);

    const droppedFiles = Array.from(event.dataTransfer.files);
    validateAndSetFiles(droppedFiles);
  };

  const handleFileSelect = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    const validFiles = [];
    const errorMessages = [];
    const extensionsToValidateVideo = ['video/mp4', 'video/quicktime', 'video/webm', 'audio/mpeg'];

    await Promise.all(selectedFiles.map(async (file) => {
      if (extensionsToValidateVideo.includes(file.type)) {
        const quality = await validateVideoQuality(file);
        if (quality === '4K') {
          errorMessages.push(`File ${file.name} is 4K. Please upload a file with a lower resolution.`);
        } else {
          validFiles.push(file);
        }
      } else {
        validFiles.push(file);
      }
    }));

    if (errorMessages.length > 0) {
      setError((prevError) => `${prevError} ${errorMessages.join('\n')}`);
    }

    if (validFiles.length > 0) {
      try {
        await validateAndSetFiles(validFiles);
      } catch (e) {
        captureException('HANDLE_FILE_SELECT_FAILED', e);
      }
    }

    if (fileRef?.current) {
      fileRef.current.value = null;
    }
  };

  const openFileDialog = () => {
    document.getElementById('fileInput').click();
  };

  return (
    <Container
      active={show}
      onClickOutside={onClose}
      page
      data-modal-uploader
    >
      <Dialog>
        <Title>{title}</Title>
        {additionalText && <Text>{additionalText}</Text>}
        <ContainerBox isError={!!error}>
          <UploadBox
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={openFileDialog}
            isDragging={isDraggingOver}
            isError={!!error}
          >
            <Text>Drag & drop one or more files here</Text>
            <AcceptedFilesText>
              Accepted formats:
              {uploadAttachment ? ' mp4, mp3, webm, mov, pdf' : ' mp4, mov, webm'}
            </AcceptedFilesText>
            <InputFile
              ref={fileRef}
              type="file"
              id="fileInput"
              multiple
              onChange={handleFileSelect}
            />
          </UploadBox>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </ContainerBox>
        {files.length > 0 && (
          <>
            <ProgressContainer>
              {files.map((file, index) => (
                <UploadFileProgress
                  key={file.name}
                  file={file}
                  // Show the upload progress if we have it
                  percent={uploadProgress[index] || 0}
                  error={errorUploadedFiles[index]}
                />
              ))}
            </ProgressContainer>
            <Text>Don&apos;t click off this page until the progress reaches 100%.</Text>
          </>
        )}
        <Text>
          By uploading videos, you agree to our
          {' '}
          <Link href={uploadPolicyUrl} target="_blank">Acceptable Use Policy</Link>
        </Text>
      </Dialog>
    </Container>
  );
};

Uploader.propTypes = {
  title: PropTypes.string.isRequired,
  additionalText: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  show: PropTypes.bool,
  onUploadCompleted: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  companyId: PropTypes.string.isRequired,
  uploadAttachment: PropTypes.bool,
};

const mapStateToProps = ({ authentication }) => ({
  companyId: authentication.company?.id,
});

Uploader.defaultProps = {
  show: false,
  additionalText: '',
  onUploadCompleted: null,
  uploadAttachment: false,
};

export default connect(mapStateToProps, null)(Uploader);
